import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Document, Page, Text, View, StyleSheet, pdf } from '@react-pdf/renderer';
import { getEnvironmentInfo, apiRequest } from '../../../config/api';
import { getCurrentTimestamp } from '../../../utils/dateUtils';
import './EmailShipment.css';

// PDF Styles - EXACT same as ShippingPrintView for consistency
const pdfStyles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 10,
    fontFamily: 'Helvetica',
  },
  header: {
    textAlign: 'center',
    marginBottom: 20,
    borderBottom: '2px solid #34495e',
    paddingBottom: 10,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 5,
  },
  shipmentNumber: {
    fontSize: 14,
    color: '#7f8c8d',
  },
  section: {
    marginBottom: 15,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 8,
    borderBottom: '1px solid #bdc3c7',
    paddingBottom: 4,
  },
  infoGrid: {
    marginBottom: 10,
  },
  infoRow: {
    flexDirection: 'row',
    marginBottom: 4,
  },
  infoLabel: {
    width: '30%',
    fontWeight: 'bold',
  },
  infoValue: {
    width: '70%',
  },
  table: {
    marginTop: 10,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#34495e',
    color: 'white',
    padding: 6,
    fontWeight: 'bold',
    fontSize: 10,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: '1px solid #bdc3c7',
    padding: 5,
    fontSize: 9,
  },
  tableRowEven: {
    backgroundColor: '#ecf0f1',
  },
  tableFooter: {
    flexDirection: 'row',
    backgroundColor: '#ecf0f1',
    padding: 6,
    fontWeight: 'bold',
    borderTop: '2px solid #34495e',
  },
  colNumber: { width: '6%' },
  colBarcode: { width: '18%' },
  colWigType: { width: '22%' },
  colCustomer: { width: '22%' },
  colInstructions: { width: '22%' },
  colWeight: { width: '10%', textAlign: 'right' },
});

// PDF Styles for Return shipments (green theme)
const returnPdfStyles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 10,
    fontFamily: 'Helvetica',
  },
  header: {
    textAlign: 'center',
    marginBottom: 20,
    borderBottom: '2px solid #27ae60',
    paddingBottom: 10,
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 5,
    color: '#27ae60',
  },
  shipmentNumber: {
    fontSize: 14,
    color: '#7f8c8d',
  },
  section: {
    marginBottom: 15,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 8,
    borderBottom: '1px solid #bdc3c7',
    paddingBottom: 4,
  },
  infoGrid: {
    marginBottom: 10,
  },
  infoRow: {
    flexDirection: 'row',
    marginBottom: 4,
  },
  infoLabel: {
    width: '30%',
    fontWeight: 'bold',
  },
  infoValue: {
    width: '70%',
  },
  table: {
    marginTop: 10,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#27ae60',
    color: 'white',
    padding: 6,
    fontWeight: 'bold',
    fontSize: 10,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: '1px solid #bdc3c7',
    padding: 5,
    fontSize: 9,
  },
  tableRowEven: {
    backgroundColor: '#e8f8f5',
  },
  tableFooter: {
    flexDirection: 'row',
    backgroundColor: '#e8f8f5',
    padding: 6,
    fontWeight: 'bold',
    borderTop: '2px solid #27ae60',
  },
  colNumber: { width: '6%' },
  colBarcode: { width: '18%' },
  colOrderNo: { width: '12%' },
  colWigType: { width: '22%' },
  colCustomer: { width: '22%' },
  colWeight: { width: '10%', textAlign: 'right' },
});

// PDF Document Component - EXACT same as ShippingPrintView
const PackingSlipPDF = ({ data }) => (
  <Document>
    <Page size="LETTER" style={pdfStyles.page}>
      {/* Header */}
      <View style={pdfStyles.header}>
        <Text style={pdfStyles.shipmentNumber}>Shipment #{data.shipmentDetails.shipmentId}</Text>
      </View>

      {/* Shipping Information */}
      <View style={pdfStyles.section}>
        <Text style={pdfStyles.sectionTitle}>Shipping Information</Text>
        <View style={pdfStyles.infoGrid}>
          <View style={pdfStyles.infoRow}>
            <Text style={pdfStyles.infoLabel}>Ship To:</Text>
            <Text style={pdfStyles.infoValue}>{data.shipmentDetails.ShippingToName}</Text>
          </View>
          <View style={pdfStyles.infoRow}>
            <Text style={pdfStyles.infoLabel}>Ship Date:</Text>
            <Text style={pdfStyles.infoValue}>
              {data.shipmentDetails.ShipOutDate
                ? new Date(data.shipmentDetails.ShipOutDate).toLocaleDateString()
                : 'N/A'}
            </Text>
          </View>
          <View style={pdfStyles.infoRow}>
            <Text style={pdfStyles.infoLabel}>Courier:</Text>
            <Text style={pdfStyles.infoValue}>{data.shipmentDetails.CourrierName || 'N/A'}</Text>
          </View>
          <View style={pdfStyles.infoRow}>
            <Text style={pdfStyles.infoLabel}>Tracking #:</Text>
            <Text style={pdfStyles.infoValue}>{data.shipmentDetails.TrackingNumber || 'N/A'}</Text>
          </View>
          {data.shipmentDetails.SpecialInstructions && (
            <View style={pdfStyles.infoRow}>
              <Text style={pdfStyles.infoLabel}>Special Instructions:</Text>
              <Text style={pdfStyles.infoValue}>{data.shipmentDetails.SpecialInstructions}</Text>
            </View>
          )}
        </View>
      </View>

      {/* Wigs Table */}
      <View style={pdfStyles.section}>
        <Text style={pdfStyles.sectionTitle}>Items in Shipment ({data.wigs.length})</Text>
        <View style={pdfStyles.table}>
          {/* Table Header */}
          <View style={pdfStyles.tableHeader}>
            <Text style={pdfStyles.colNumber}>#</Text>
            <Text style={pdfStyles.colBarcode}>Barcode</Text>
            <Text style={pdfStyles.colWigType}>Wig Type</Text>
            <Text style={pdfStyles.colCustomer}>Customer</Text>
            <Text style={pdfStyles.colInstructions}>Instructions</Text>
            <Text style={pdfStyles.colWeight}>Weight (g)</Text>
          </View>

          {/* Table Rows */}
          {data.wigs.map((wig, index) => (
            <View
              key={wig.Number || index}
              style={[pdfStyles.tableRow, index % 2 === 1 ? pdfStyles.tableRowEven : {}]}
            >
              <Text style={pdfStyles.colNumber}>{wig.Number || index + 1}</Text>
              <Text style={pdfStyles.colBarcode}>{wig.Wig_Bar_Code}</Text>
              <Text style={pdfStyles.colWigType}>{wig.OrderWigType}</Text>
              <Text style={pdfStyles.colCustomer}>{wig.OrderCustomerName}</Text>
              <Text style={pdfStyles.colInstructions}>{wig.ShippingInstructions || '-'}</Text>
              <Text style={pdfStyles.colWeight}>
                {wig.WigWeight ? parseFloat(wig.WigWeight).toFixed(3) : '0.000'}
              </Text>
            </View>
          ))}

          {/* Table Footer */}
          <View style={pdfStyles.tableFooter}>
            <Text style={{ width: '90%', textAlign: 'right' }}>Total Weight:</Text>
            <Text style={pdfStyles.colWeight}>{data.totalWeight} g</Text>
          </View>
        </View>
      </View>
    </Page>
  </Document>
);

// Return PDF Document Component (green theme)
const ReturnPackingSlipPDF = ({ data }) => (
  <Document>
    <Page size="LETTER" style={returnPdfStyles.page}>
      {/* Header */}
      <View style={returnPdfStyles.header}>
        <Text style={returnPdfStyles.title}>RETURN SHIPMENT RECEIPT</Text>
        <Text style={returnPdfStyles.shipmentNumber}>Return #{data.shipmentDetails.shipmentId}</Text>
      </View>

      {/* Return Information */}
      <View style={returnPdfStyles.section}>
        <Text style={returnPdfStyles.sectionTitle}>Return Shipment Information</Text>
        <View style={returnPdfStyles.infoGrid}>
          <View style={returnPdfStyles.infoRow}>
            <Text style={returnPdfStyles.infoLabel}>Received At:</Text>
            <Text style={returnPdfStyles.infoValue}>{data.shipmentDetails.ShippingToName}</Text>
          </View>
          <View style={returnPdfStyles.infoRow}>
            <Text style={returnPdfStyles.infoLabel}>Return Date:</Text>
            <Text style={returnPdfStyles.infoValue}>
              {data.shipmentDetails.ShipReturnDate
                ? new Date(data.shipmentDetails.ShipReturnDate).toLocaleDateString()
                : 'N/A'}
            </Text>
          </View>
          <View style={returnPdfStyles.infoRow}>
            <Text style={returnPdfStyles.infoLabel}>Courier:</Text>
            <Text style={returnPdfStyles.infoValue}>{data.shipmentDetails.CourrierName || 'N/A'}</Text>
          </View>
          <View style={returnPdfStyles.infoRow}>
            <Text style={returnPdfStyles.infoLabel}>Tracking #:</Text>
            <Text style={returnPdfStyles.infoValue}>{data.shipmentDetails.TrackingNumber || 'N/A'}</Text>
          </View>
          {data.shipmentDetails.SpecialInstructions && (
            <View style={returnPdfStyles.infoRow}>
              <Text style={returnPdfStyles.infoLabel}>Notes:</Text>
              <Text style={returnPdfStyles.infoValue}>{data.shipmentDetails.SpecialInstructions}</Text>
            </View>
          )}
        </View>
      </View>

      {/* Returned Items Table */}
      <View style={returnPdfStyles.section}>
        <Text style={returnPdfStyles.sectionTitle}>Items Received ({data.wigs.length})</Text>
        <View style={returnPdfStyles.table}>
          {/* Table Header */}
          <View style={returnPdfStyles.tableHeader}>
            <Text style={returnPdfStyles.colNumber}>#</Text>
            <Text style={returnPdfStyles.colBarcode}>Barcode</Text>
            <Text style={returnPdfStyles.colOrderNo}>Order #</Text>
            <Text style={returnPdfStyles.colCustomer}>Customer</Text>
            <Text style={returnPdfStyles.colWigType}>Wig Type</Text>
            <Text style={returnPdfStyles.colWeight}>Weight (g)</Text>
          </View>

          {/* Table Rows */}
          {data.wigs.map((wig, index) => (
            <View
              key={wig.barcode || index}
              style={[returnPdfStyles.tableRow, index % 2 === 1 ? returnPdfStyles.tableRowEven : {}]}
            >
              <Text style={returnPdfStyles.colNumber}>{wig.Number || index + 1}</Text>
              <Text style={returnPdfStyles.colBarcode}>{wig.barcode || wig.Wig_Bar_Code}</Text>
              <Text style={returnPdfStyles.colOrderNo}>{wig.OrderNo || '-'}</Text>
              <Text style={returnPdfStyles.colCustomer}>{wig.customerName || wig.OrderCustomerName || '-'}</Text>
              <Text style={returnPdfStyles.colWigType}>{wig.wigType || wig.OrderWigType || '-'}</Text>
              <Text style={returnPdfStyles.colWeight}>
                {wig.wigWeight || wig.WigWeight ? parseFloat(wig.wigWeight || wig.WigWeight).toFixed(3) : '0.000'}
              </Text>
            </View>
          ))}

          {/* Table Footer */}
          <View style={returnPdfStyles.tableFooter}>
            <Text style={{ width: '90%', textAlign: 'right' }}>Total Weight:</Text>
            <Text style={returnPdfStyles.colWeight}>{data.totalWeight} g</Text>
          </View>
        </View>
      </View>
    </Page>
  </Document>
);

// Environment-aware n8n webhook URL configuration
// Note: Using production webhook for all environments because webhook-test has CORS restrictions
const getN8nWebhookUrl = () => {
  // Always use the production webhook URL (works from all origins)
  return "https://gmc-space.app.n8n.cloud/webhook/shipment-email";
};

const EmailShipment = ({
  shipmentId,
  shipmentDetails,
  wigs = [],
  wigCount,
  onSuccess = () => {},
  onError = () => {},
  onClose,
  isOpen,
  isReturn = false
}) => {
  const [recipients, setRecipients] = useState(['yudiweinberg@gmail.com','rm3102468@gmail.com']);
  const [newEmail, setNewEmail] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);

  const envInfo = getEnvironmentInfo();

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setRecipients(['yudiweinberg@gmail.com','rm3102468@gmail.com']);
      setNewEmail('');
      setCustomMessage('');
      setError(null);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleAddEmail = () => {
    const email = newEmail.trim().toLowerCase();

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Check for duplicates
    if (recipients.includes(email)) {
      setError('This email is already in the list');
      return;
    }

    setRecipients([...recipients, email]);
    setNewEmail('');
    setError(null);
  };

  const handleRemoveEmail = (emailToRemove) => {
    if (recipients.length === 1) {
      setError('At least one recipient is required');
      return;
    }
    setRecipients(recipients.filter(email => email !== emailToRemove));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEmail();
    }
  };

  // Generate PDF using @react-pdf/renderer
  const generatePdfBase64 = async () => {
    const totalWeight = wigs.reduce((sum, wig) => sum + (parseFloat(wig.WigWeight || wig.wigWeight) || 0), 0).toFixed(3);

    // Prepare data based on shipment type
    const pdfData = {
      shipmentDetails: {
        shipmentId: shipmentId,
        ShippingToName: shipmentDetails?.ShippingToName || 'N/A',
        ShipOutDate: shipmentDetails?.ShipOutDate,
        ShipReturnDate: shipmentDetails?.ShipReturnDate,
        CourrierName: shipmentDetails?.CourrierName,
        TrackingNumber: shipmentDetails?.TrackingNumber,
        SpecialInstructions: shipmentDetails?.SpecialInstructions
      },
      wigs: wigs.map((wig, index) => ({
        ...wig,
        Number: wig.Number || index + 1
      })),
      totalWeight: totalWeight
    };

    // Generate PDF using appropriate component based on shipment type
    const doc = isReturn ? <ReturnPackingSlipPDF data={pdfData} /> : <PackingSlipPDF data={pdfData} />;
    const asPdf = pdf(doc);
    const blob = await asPdf.toBlob();

    // Convert blob to base64
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        // Remove data URL prefix to get pure base64
        const base64 = reader.result.split(',')[1];
        resolve({
          pdfBase64: base64,
          size: blob.size
        });
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const handleSendEmail = async () => {
    if (recipients.length === 0) {
      setError('At least one recipient is required');
      return;
    }

    setIsSending(true);
    setError(null);

    try {
      console.log('📧 Preparing to send shipment email:', {
        environment: envInfo.environment,
        shipmentId: shipmentId,
        recipients: recipients,
        wigsCount: wigs.length
      });

      // Step 1: Generate PDF using @react-pdf/renderer (SAME as print view)
      console.log('📄 Generating PDF using @react-pdf/renderer...');
      const pdfResult = await generatePdfBase64();

      console.log('✅ PDF generated successfully:', {
        size: pdfResult.size
      });

      // Step 3: Build email HTML for the email body
      const entryDate = shipmentDetails?.EntryDate
        ? new Date(shipmentDetails.EntryDate).toLocaleDateString()
        : 'N/A';

      const shipOutDate = shipmentDetails?.ShipOutDate
        ? new Date(shipmentDetails.ShipOutDate).toLocaleDateString()
        : 'N/A';

      const returnDate = shipmentDetails?.ShipReturnDate
        ? new Date(shipmentDetails.ShipReturnDate).toLocaleDateString()
        : 'N/A';

      const totalWeight = wigs.reduce((sum, wig) => sum + (parseFloat(wig.WigWeight || wig.wigWeight) || 0), 0);

      // Theme colors based on shipment type
      const themeColor = isReturn ? '#27ae60' : '#34495e';

      // Generate items preview table rows
      const itemsPreviewRows = wigs.map((wig, index) => `
        <tr style="border-bottom: 1px solid #eee;">
          <td style="padding: 8px; text-align: center; font-size: 12px;">${index + 1}</td>
          <td style="padding: 8px; font-size: 12px;">${wig.Wig_Bar_Code || wig.barcode || '-'}</td>
          <td style="padding: 8px; font-size: 12px;">${wig.OrderWigType || wig.wigType || '-'}</td>
          <td style="padding: 8px; font-size: 12px;">${wig.OrderCustomerName || wig.customerName || '-'}</td>
        </tr>
      `).join('');

      const emailBodyHtml = `
        <!DOCTYPE html>
        <html>
        <head>
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
            .container { max-width: 700px; margin: 0 auto; padding: 20px; }
            .header { background: ${themeColor}; color: white; padding: 20px; text-align: center; border-radius: 8px 8px 0 0; }
            .header h1 { margin: 0; font-size: 24px; }
            .content { background: #f9f9f9; padding: 20px; border-radius: 0 0 8px 8px; }
            .info-box { background: white; padding: 15px; margin: 15px 0; border-radius: 5px; border-left: 4px solid ${themeColor}; }
            .info-row { margin: 8px 0; }
            .info-label { font-weight: bold; }
            .custom-message { background: #fff3cd; padding: 15px; margin: 15px 0; border-radius: 5px; border-left: 4px solid #ffc107; }
            .items-section { margin-top: 20px; }
            .items-section h3 { margin: 0 0 10px 0; color: #333; font-size: 16px; }
            .items-table { width: 100%; border-collapse: collapse; background: white; border-radius: 5px; overflow: hidden; }
            .items-table th { background: ${themeColor}; color: white; padding: 10px 8px; text-align: left; font-size: 12px; }
            .items-table td { padding: 8px; font-size: 12px; }
            .items-table tr:nth-child(even) { background: ${isReturn ? '#e8f8f5' : '#f8f8f8'}; }
            .footer { text-align: center; margin-top: 20px; color: #666; font-size: 12px; }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h1>${isReturn ? 'Return' : 'Shipment'} #${shipmentId}</h1>
              <p>${isReturn ? 'Return Receipt Attached' : 'Packing Slip Attached'}</p>
            </div>
            <div class="content">
              <div class="info-box">
                <div class="info-row"><span class="info-label">${isReturn ? 'Received At:' : 'Ship To:'}</span> ${shipmentDetails?.ShippingToName || 'N/A'}</div>
                <div class="info-row"><span class="info-label">Entry Date:</span> ${entryDate}</div>
                <div class="info-row"><span class="info-label">${isReturn ? 'Return Date:' : 'Ship Date:'}</span> ${isReturn ? returnDate : shipOutDate}</div>
                <div class="info-row"><span class="info-label">Courier:</span> ${shipmentDetails?.CourrierName || 'N/A'}</div>
                <div class="info-row"><span class="info-label">Tracking #:</span> ${shipmentDetails?.TrackingNumber || 'N/A'}</div>
                <div class="info-row"><span class="info-label">Total Items:</span> ${wigs.length}</div>
                <div class="info-row"><span class="info-label">Total Weight:</span> ${totalWeight.toFixed(3)} g</div>
              </div>
              ${customMessage ? `
                <div class="custom-message">
                  <strong>Note:</strong> ${customMessage}
                </div>
              ` : ''}

              <!-- Items Preview Table -->
              <div class="items-section">
                <h3>${isReturn ? 'Items Received' : 'Items in Shipment'} (${wigs.length})</h3>
                <table class="items-table">
                  <thead>
                    <tr>
                      <th style="width: 40px;">#</th>
                      <th>Barcode</th>
                      <th>Wig Type</th>
                      <th>Customer</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${itemsPreviewRows}
                  </tbody>
                </table>
              </div>

              <p style="margin-top: 20px; color: #666;">Full ${isReturn ? 'return receipt' : 'packing slip'} with all details is attached as a PDF.</p>
            </div>
            <div class="footer">
              <p>This email was sent from DiNi Tracker - Rachel's Room</p>
            </div>
          </div>
        </body>
        </html>
      `;

      // Step 4: Send to n8n webhook
      const webhookUrl = getN8nWebhookUrl();
      console.log('📧 Sending email via n8n webhook:', webhookUrl);

      const emailData = {
        // Recipients
        customerEmail: recipients.join(', '),
        recipients: recipients,

        // Email content
        subject: `${isReturn ? 'Return' : 'Shipment'} #${shipmentId} - ${isReturn ? 'Return Receipt' : 'Packing Slip'}`,
        html: emailBodyHtml,

        // PDF attachment (base64 encoded) - Generated using same renderer as print view
        pdfAttachment: {
          filename: `${isReturn ? 'Return' : 'Shipment'}-${shipmentId}-${isReturn ? 'Receipt' : 'PackingSlip'}.pdf`,
          content: pdfResult.pdfBase64,
          encoding: 'base64',
          contentType: 'application/pdf'
        },

        // Shipment info
        shipmentId: shipmentId,
        customMessage: customMessage.trim(),

        // Environment info
        environment: envInfo.environment,
        sentAt: getCurrentTimestamp(),
        sentFrom: 'DiniTracker - Rachel\'s Room'
      };

      const response = await apiRequest(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Environment': envInfo.environment,
          'X-Source': 'DiniTracker-EmailShipment'
        },
        body: JSON.stringify(emailData)
      });

      // Handle response
      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            errorMessage = errorData.message || errorData.error || errorMessage;
          } else {
            const errorText = await response.text();
            errorMessage = errorText || errorMessage;
          }
        } catch (parseError) {
          console.warn('Could not parse error response:', parseError);
        }

        throw new Error(errorMessage);
      }

      // Handle successful response
      let result = { success: true, message: 'Email sent successfully' };

      try {
        const contentType = response.headers.get('content-type');

        if (contentType && contentType.includes('application/json')) {
          const responseText = await response.text();

          if (responseText && responseText.trim()) {
            result = JSON.parse(responseText);
          }
        }
      } catch (parseError) {
        console.warn('Could not parse success response JSON, but request succeeded:', parseError);
      }

      if (result && result.success === false) {
        throw new Error(result.message || result.error || 'Email sending failed');
      }

      console.log('✅ Email sent successfully:', result);

      onSuccess({
        recipients: recipients,
        shipmentId: shipmentId,
        result: result
      });

      alert(`Email sent successfully to ${recipients.length} recipient(s)!`);
      onClose();

    } catch (err) {
      console.error('❌ Error sending email:', err);

      let userMessage = err.message;

      if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
        userMessage = `Network error (${envInfo.environment}). Please check your connection and try again.`;
      } else if (err.message.includes('404')) {
        userMessage = `Email service unavailable (${envInfo.environment}). Please try again later.`;
      } else if (err.message.includes('timeout')) {
        userMessage = 'Request timed out. Please try again.';
      }

      setError(userMessage);
      onError(err);
    } finally {
      setIsSending(false);
    }
  };

  if (!isOpen) return null;

  const entryDate = shipmentDetails?.EntryDate
    ? new Date(shipmentDetails.EntryDate).toLocaleDateString()
    : 'N/A';

  return createPortal(
    <div className="email-shipment-overlay" onClick={(e) => e.stopPropagation()}>
      <div className="email-shipment-modal">
        <div className="email-shipment-header">
          <h3>Email {isReturn ? 'Return' : 'Shipment'} #{shipmentId}</h3>
          <button
            onClick={onClose}
            className="email-shipment-close-btn"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="email-shipment-content">
          {error && (
            <div className="email-shipment-error">
              {error}
            </div>
          )}

          {/* Recipients Section */}
          <div className="email-shipment-recipients">
            <label className="recipients-label">Recipients:</label>

            {/* Current Recipients */}
            <div className="recipients-list">
              {recipients.map((email, index) => (
                <div key={email} className="recipient-chip">
                  <span>{email}</span>
                  <button
                    onClick={() => handleRemoveEmail(email)}
                    className="remove-recipient-btn"
                    title="Remove recipient"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>

            {/* Add New Recipient */}
            <div className="add-recipient-row">
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Add another email..."
                className="add-recipient-input"
              />
              <button
                onClick={handleAddEmail}
                className="add-recipient-btn"
                disabled={!newEmail.trim()}
              >
                + Add
              </button>
            </div>
          </div>

          {/* Custom Message */}
          <div className="email-shipment-message">
            <label htmlFor="customMessage">Custom Message (Optional):</label>
            <textarea
              id="customMessage"
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              placeholder="Add a message to include with the shipment email..."
              rows="3"
              maxLength="500"
            />
            <small>{customMessage.length}/500 characters</small>
          </div>

          {/* Email Preview Info */}
          <div className="email-preview-info">
            <p><strong>Email will include:</strong></p>
            <ul>
              <li>Shipment details summary</li>
              <li>Complete list of items with barcodes</li>
              <li>PDF packing slip attachment</li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="email-shipment-actions">
            <button
              onClick={onClose}
              className="email-shipment-cancel-btn"
              disabled={isSending}
            >
              Cancel
            </button>
            <button
              onClick={handleSendEmail}
              className="email-shipment-send-btn"
              disabled={isSending || recipients.length === 0}
            >
              {isSending ? (
                <>
                  <span className="spinner"></span>
                  Sending...
                </>
              ) : (
                `Send to ${recipients.length} recipient${recipients.length !== 1 ? 's' : ''}`
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EmailShipment;
